import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { StatsCard } from "@/components/dashboard/StatsCard";
import { Leaderboard } from "@/components/dashboard/Leaderboard";
import { TaskList } from "@/components/dashboard/TaskList";
import { IncentiveWidget } from "@/components/dashboard/IncentiveWidget";
import { DailyCommitmentCard } from "@/components/dashboard/DailyCommitmentCard";
import { AddTaskDialog } from "@/components/dashboard/AddTaskDialog";
import { CreateLeadSheet } from "@/components/leads/CreateLeadSheet";
import { MobileHomeSummary } from "@/components/dashboard/MobileHomeSummary";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useLeads } from "@/hooks/useLeads";
import { useAuth } from "@/contexts/AuthContext";
import { useModal } from "@/contexts/ModalContext";
import { useCommitments } from "@/hooks/useCommitments";
import { useDsaPartners } from "@/hooks/useDsaPartners";
import { getAttendanceStatus } from "@/services/attendanceService";
import { getUserById } from "@/services/userService";
import { isHoliday as checkOfficeHoliday } from "@/lib/holidayUtils";
import { isRelationalManager, isBasicUser } from "@/lib/rbac";
import { Users, LogIn, TrendingUp, Plus, UserPlus, Bell, Trophy } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { startOfMonth, endOfMonth, startOfDay, endOfDay, isWithinInterval } from "date-fns";
import { db } from "@/lib/firebase";
import { collection, getDocs, query, where, Timestamp, doc, onSnapshot } from "firebase/firestore";

function formatCurrency(amount: number) {
  if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  return `₹${amount.toLocaleString("en-IN")}`;
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good Morning";
  if (hour < 17) return "Good Afternoon";
  return "Good Evening";
}

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { leads } = useLeads();
  const { user } = useAuth();
  const { monthlyCommitment } = useCommitments();
  const isRM = isRelationalManager(user);
  const isBasic = isBasicUser(user);
  // Only subscribe to DSA partners for Relational Manager — avoids unnecessary Firestore listeners for all other roles
  const { partners: dsaPartners } = useDsaPartners({ enabled: isRM });
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [createLeadOpen, setCreateLeadOpen] = useState(false);
  const [commitmentKey, setCommitmentKey] = useState(0);
  const [incentiveDialogOpen, setIncentiveDialogOpen] = useState(false);
  const [attendanceDialogOpen, setAttendanceDialogOpen] = useState(false);
  const { registerModal, unregisterModal } = useModal();
  useEffect(() => {
    if (!incentiveDialogOpen) return;
    registerModal('dashboard-incentive-dialog', () => setIncentiveDialogOpen(false), 1);
    return () => unregisterModal('dashboard-incentive-dialog');
  }, [incentiveDialogOpen, registerModal, unregisterModal]);
  useEffect(() => {
    if (!attendanceDialogOpen) return;
    registerModal('dashboard-attendance-dialog', () => setAttendanceDialogOpen(false), 1);
    return () => unregisterModal('dashboard-attendance-dialog');
  }, [attendanceDialogOpen, registerModal, unregisterModal]);
  const [attendanceLabel, setAttendanceLabel] = useState<'attendance' | 'present' | 'absent' | 'holiday'>('attendance');
  const [vendorVisitsToday, setVendorVisitsToday] = useState(0);
  const [loginStats, setLoginStats] = useState({ count: 0, amount: 0 });
  // State for live office holiday tracking (separate from DailyCommitmentCard)
  const [liveOfficeId, setLiveOfficeId] = useState<string>('');
  
  // Pull to refresh state
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  
  // Auto-open attendance dialog if redirected from login with ?openAttendance=true
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('openAttendance') === 'true') {
      setAttendanceDialogOpen(true);
      params.delete('openAttendance');
      const remaining = params.toString();
      navigate(remaining ? `/dashboard?${remaining}` : '/dashboard', { replace: true });
    }
  }, [location.search, navigate]);

  // Refresh stats when commitment changes
  const refreshCommitment = () => {
    setCommitmentKey(prev => prev + 1);
  };

  // Load attendance status for the button label
  const loadAttendanceStatus = useCallback(async () => {
    if (!user?.userId) return;
    try {
      const status = await getAttendanceStatus(user.userId);
      if (status.status === 'holiday') {
        setAttendanceLabel('holiday');
      } else if (status.isAbsent) {
        setAttendanceLabel('absent');
      } else if (status.hasCheckedIn) {
        setAttendanceLabel('present');
      } else {
        setAttendanceLabel('attendance');
      }
    } catch (error) {
      console.error('Error loading attendance status:', error);
    }
  }, [user?.userId]);

  useEffect(() => {
    loadAttendanceStatus();
    
    // Auto-refresh attendance status at midnight to detect holiday changes
    const now = new Date();
    const midnight = new Date(now);
    midnight.setDate(midnight.getDate() + 1);
    midnight.setHours(0, 0, 5, 0); // 5 seconds past midnight
    const msUntilMidnight = midnight.getTime() - now.getTime();
    
    const midnightTimer = setTimeout(() => {
      loadAttendanceStatus();
    }, msUntilMidnight);
    
    return () => clearTimeout(midnightTimer);
  }, [loadAttendanceStatus]);

  // Load user's officeId once so we can subscribe to office holiday changes
  useEffect(() => {
    if (!user?.userId || !user.documentId) return;
    getUserById(user.documentId).then(userData => {
      if (userData?.officeId) setLiveOfficeId(userData.officeId);
    }).catch(() => {});
  }, [user?.userId, user?.documentId]);

  // Live Firestore listener on office document — keeps attendanceLabel in sync
  // when admin adds/removes a holiday for the user's office
  useEffect(() => {
    if (!liveOfficeId) return;
    const officeDocRef = doc(db, 'offices', liveOfficeId);
    const unsub = onSnapshot(officeDocRef, (snap) => {
      if (snap.exists()) {
        const holidayConfig = snap.data().holidayConfig;
        const h = checkOfficeHoliday(new Date(), holidayConfig, true);
        if (h.isHoliday) {
          // Admin made today a holiday — override whatever the DB attendance says
          setAttendanceLabel('holiday');
        } else {
          // Holiday removed — reload actual attendance status from DB
          loadAttendanceStatus();
        }
      }
    }, () => {/* ignore errors silently */});
    return () => unsub();
  }, [liveOfficeId, loadAttendanceStatus]);

  // Load vendor visits for today
  const loadVendorVisits = useCallback(async () => {
    if (!user?.userId) return;
    try {
      const uid = user.userId || user.id;
      // Run two rules-compliant queries and merge (avoids full-collection scan)
      const [createdSnap, sharedSnap] = await Promise.all([
        getDocs(query(collection(db, 'channelPartners'), where('createdBy', '==', uid))),
        getDocs(query(collection(db, 'channelPartners'), where('sharedWith', 'array-contains', uid))),
      ]);
      const docsMap = new Map();
      [...createdSnap.docs, ...sharedSnap.docs].forEach(d => docsMap.set(d.id, d));
      
      const today = startOfDay(new Date());
      const tomorrow = endOfDay(new Date());
      
      let vendorVisits = 0;
      docsMap.forEach(doc => {
        const data = doc.data();
        
        if (data.lastVisitDate) {
          const lastVisit = data.lastVisitDate instanceof Timestamp 
            ? data.lastVisitDate.toDate() 
            : new Date(data.lastVisitDate);
          if (lastVisit >= today && lastVisit <= tomorrow) {
            // Count vendor visits for today BY CURRENT USER
            // If lastVisitedBy is set, use it; otherwise fall back to createdBy
            const visitedByCurrentUser = data.lastVisitedBy
              ? data.lastVisitedBy === uid
              : (data.createdBy === uid);
            if (visitedByCurrentUser) {
              vendorVisits++;
            }
          }
        }
      });
      
      setVendorVisitsToday(vendorVisits);
    } catch (error) {
      console.error('Error loading vendor visits:', error);
    }
  }, [user?.userId, user?.id]);

  useEffect(() => {
    loadVendorVisits();
  }, [loadVendorVisits]);

  // Load login stats from leadStageHistory
  useEffect(() => {
    if (!user?.userId) return;
    
    const loadLoginStats = async () => {
      try {
        const now = new Date();
        const monthStart = startOfMonth(now);
        const monthEnd = endOfMonth(now);
        const uid = user.userId || user.id;
        
        const loginAndBeyondStatuses = [
          'logged_in', 'query', 'sanctioned', 'verification_pending',
          'disbursal_pending', 'partial_disbursed', 'disbursed',
          'otc', 'payout_released', 'closed'
        ];
        
        let loginCount = 0;
        let loginAmount = 0;
        
        leads.forEach(lead => {
          if (lead.assignedTo !== uid && lead.createdBy !== uid) return;
          if (!loginAndBeyondStatuses.includes(lead.status)) return;
          const updatedAt = lead.updatedAt instanceof Date ? lead.updatedAt : new Date(lead.updatedAt);
          if (isWithinInterval(updatedAt, { start: monthStart, end: monthEnd })) {
            loginCount++;
            loginAmount += lead.loanAmount || 0;
          }
        });
        
        setLoginStats({
          count: loginCount,
          amount: loginAmount,
        });
      } catch (error) {
        console.error('Error loading login stats:', error);
        setLoginStats({ count: 0, amount: 0 });
      }
    };
    
    loadLoginStats();
  }, [user?.userId, user?.name, user?.id, leads, commitmentKey]);
  
  // Calculate stats - exclude completed stages from active pipeline
  const activeLeads = leads.filter(l => !['partial_disbursed', 'disbursed', 'rejected', 'otc', 'payout_released', 'closed', 'removed', 'followup_later'].includes(l.status)).length;

  const leadsAddedToday = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const uid = user?.userId || user?.id;
    
    return leads.filter(lead => {
      if (lead.createdBy !== uid) return false;
      const createdAt = lead.createdAt instanceof Date ? lead.createdAt : new Date(lead.createdAt);
      return createdAt >= today && createdAt < tomorrow;
    }).length;
  }, [leads, user?.userId, user?.id]);

  // DSA partners added this month — derived once, reused in desktop RM block and MobileHomeSummary prop
  const dsaPartnersThisMonth = useMemo(() => {
    if (!isRM || !dsaPartners) return 0;
    const now = new Date();
    return dsaPartners.filter(p => {
      if (!p.createdAt) return false;
      const d = p.createdAt instanceof Date ? p.createdAt : new Date(p.createdAt);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length;
  }, [isRM, dsaPartners]);

  const actualStats = useMemo(() => {
    if (!user) {
      return {
        loginCount: 0,
        loginAmount: 0,
        loginTargetCount: 0,
        loginTargetAmount: 0,
        disbursementCount: 0,
        disbursementAmount: 0,
        disbursementTargetCount: 0,
        disbursementTargetAmount: 0,
      };
    }
    
    const now = new Date();
    const monthStart = startOfMonth(now);
    const monthEnd = endOfMonth(now);
    
    const loginCount = loginStats.count;
    const loginAmount = loginStats.amount;
    
    let disbursementCount = 0;
    let disbursementAmount = 0;
    
    const uid = user.userId || user.id;
    leads.forEach(lead => {
      const ownerId = lead.createdBy || lead.assignedTo;
      if (ownerId !== uid) return;
      
      if (lead.disbursements && lead.disbursements.length > 0) {
        let leadMonthAmount = 0;
        let hasMonthDisbursement = false;
        lead.disbursements.forEach(disbursement => {
          const considerationDate = disbursement.salesConsiderationDate 
            ? new Date(disbursement.salesConsiderationDate)
            : disbursement.disbursementDate;
            
          if (isWithinInterval(considerationDate, { start: monthStart, end: monthEnd })) {
            leadMonthAmount += disbursement.disbursedAmount;
            hasMonthDisbursement = true;
          }
        });
        if (hasMonthDisbursement) {
          disbursementCount += 1;
          disbursementAmount += leadMonthAmount;
        }
      } else if (lead.status === 'disbursed' && lead.disbursedAmount) {
        const considerationDate = lead.salesConsiderationDate 
          ? new Date(lead.salesConsiderationDate)
          : lead.updatedAt;
          
        if (isWithinInterval(considerationDate, { start: monthStart, end: monthEnd })) {
          disbursementCount += 1;
          disbursementAmount += lead.disbursedAmount;
        }
      }
    });
    
    return {
      loginCount,
      loginAmount,
      loginTargetCount: monthlyCommitment?.loginFiles || 0,
      loginTargetAmount: monthlyCommitment?.loginAmount || 0,
      disbursementCount,
      disbursementAmount,
      disbursementTargetCount: monthlyCommitment?.disbursementFiles || 0,
      disbursementTargetAmount: monthlyCommitment?.disbursementAmount || 0,
    };
  }, [leads, user, commitmentKey, loginStats, monthlyCommitment]);

  const handleStatsClick = (type: 'leads' | 'login' | 'disbursed') => {
    if (type === 'leads') {
      navigate('/leads', { state: { filter: 'all' } });
    } else if (type === 'login') {
      navigate('/leads', { state: { filter: 'logged_in' } });
    } else if (type === 'disbursed') {
      navigate('/leads', { state: { filter: 'disbursed' } });
    }
  };

  // Pull to refresh handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    const scrollContainer = (e.currentTarget as HTMLElement);
    if (scrollContainer.scrollTop === 0) {
      setTouchStart(e.touches[0].clientY);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStart === 0) return;
    
    const scrollContainer = (e.currentTarget as HTMLElement);
    const currentTouch = e.touches[0].clientY;
    const distance = currentTouch - touchStart;
    
    if (distance > 0 && scrollContainer.scrollTop === 0) {
      setPullDistance(Math.min(distance, 120));
      if (distance > 80) {
        setIsPulling(true);
      }
    } else {
      setPullDistance(0);
      setTouchStart(0);
    }
  };

  const handleTouchEnd = async () => {
    if (isPulling && pullDistance > 80) {
      try {
        refreshCommitment();
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }
        toast.success("Refreshed", { description: "Dashboard data updated successfully" });
      } catch (error) {
        console.error("Refresh error:", error);
      }
    }
    
    setIsPulling(false);
    setPullDistance(0);
    setTouchStart(0);
  };

  // Reusable attendance button label helper
  const attendanceButtonLabel =
    attendanceLabel === 'present' ? '✓ Present'
    : attendanceLabel === 'absent' ? '✗ Absent'
    : attendanceLabel === 'holiday' ? '🌴 Holiday'
    : 'Attendance';

  return (
    <AppLayout>
      <div 
        className="p-4 md:p-6 lg:p-8 pt-6 space-y-2 md:space-y-3 max-w-7xl mx-auto pb-32 md:pb-8 overflow-x-hidden"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Pull to Refresh Indicator */}
        {pullDistance > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: pullDistance / 120 }}
            className="md:hidden absolute top-0 left-0 right-0 h-16 flex items-center justify-center bg-gradient-to-b from-green-50 to-transparent dark:from-green-950"
            style={{ transform: `translateY(${Math.min(pullDistance - 80, 40)}px)` }}
          >
            <motion.div
              animate={{ rotate: isPulling ? 360 : pullDistance * 3 }}
              transition={{ duration: isPulling ? 1 : 0, repeat: isPulling ? Infinity : 0, ease: "linear" }}
              className="text-green-600"
            >
              <TrendingUp className="h-6 w-6" />
            </motion.div>
          </motion.div>
        )}

        {/* Header - Desktop Only (non-RM, non-Basic) */}
        {!isRM && !isBasic && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="hidden md:block"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0 space-y-1">
                <h1 className="text-lg sm:text-xl md:text-2xl font-bold tracking-tight">
                  {getGreeting()}, {user?.name.split(" ")[0] || 'User'}! 👋
                </h1>
                <p className="text-sm sm:text-base text-muted-foreground">
                  Here's what's happening with your pipeline today.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Desktop RM Summary - Desktop Only */}
        {isRM && (
          <div className="hidden md:block">
            {/* Greeting + Attendance button */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-xl md:text-2xl font-bold tracking-tight">
                  {getGreeting()}, {user?.name.split(" ")[0] || 'User'}! 👋
                </h1>
                <p className="text-sm text-muted-foreground">
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'short' })}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAttendanceDialogOpen(true)}
                className="border-green-500 text-green-700 hover:bg-green-50"
              >
                {attendanceButtonLabel}
              </Button>
            </div>

            {/* DSA Partners + Add Reminder cards */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-white rounded-2xl border border-border p-5 shadow-sm flex flex-col items-center justify-center text-center">
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-orange-100">
                    <Plus className="h-4 w-4 text-orange-600" />
                  </div>
                  <span className="text-xs text-muted-foreground uppercase font-medium tracking-wide">DSA Partners</span>
                </div>
                <p className="text-3xl font-bold text-orange-600">
                  {dsaPartnersThisMonth}
                </p>
                <p className="text-sm text-muted-foreground mt-1">Added This Month</p>
              </div>
              <div
                className="bg-white rounded-2xl border border-green-200 p-5 shadow-sm flex flex-col items-center justify-center gap-2 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setAddTaskOpen(true)}
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-green-100">
                  <Bell className="h-4 w-4 text-green-600" />
                </div>
                <p className="text-sm font-semibold text-green-600">Add Reminder</p>
              </div>
            </div>
          </div>
        )}

        {/* Desktop Basic User - Greeting + Attendance only */}
        {isBasic && (
          <div className="hidden md:block mb-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl md:text-2xl font-bold tracking-tight">
                  {getGreeting()}, {user?.name.split(" ")[0] || 'User'}! 👋
                </h1>
                <p className="text-sm text-muted-foreground">
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'short' })}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setAttendanceDialogOpen(true)}
                className="border-green-500 text-green-700 hover:bg-green-50"
              >
                {attendanceButtonLabel}
              </Button>
            </div>
          </div>
        )}

        {/* Mobile Home Summary Card - Mobile Only */}
        <MobileHomeSummary
          greeting={getGreeting()}
          userName={user?.name.split(" ")[0] || 'User'}
          activeLeads={activeLeads}
          loginCount={actualStats.loginCount}
          loginAmount={actualStats.loginAmount}
          loginTargetCount={actualStats.loginTargetCount}
          loginTargetAmount={actualStats.loginTargetAmount}
          disbursementCount={actualStats.disbursementCount}
          disbursementAmount={actualStats.disbursementAmount}
          disbursementTargetCount={actualStats.disbursementTargetCount}
          disbursementTargetAmount={actualStats.disbursementTargetAmount}
          leadsAddedToday={leadsAddedToday}
          vendorVisitsToday={vendorVisitsToday}
          incentiveEarned={0}
          onAddLead={() => setCreateLeadOpen(true)}
          onAddReminder={() => setAddTaskOpen(true)}
          onIncentiveClick={() => setIncentiveDialogOpen(true)}
          onAttendanceClick={() => setAttendanceDialogOpen(true)}
          attendanceLabel={attendanceLabel}
          isRelationalManager={isRM}
          isBasicUser={isBasic}
          dsaPartnersTotal={dsaPartners?.length || 0}
          dsaPartnersThisMonth={dsaPartnersThisMonth}
        />

        {/* Stats Grid - 3 Cards - Clickable - Desktop Only */}
        {!isRM && !isBasic && (
          <div className="hidden md:grid grid-cols-3 gap-1.5 sm:gap-2 md:gap-4">
            <div onClick={() => handleStatsClick('leads')} className="cursor-pointer">
              <StatsCard title="Leads" value={activeLeads.toString()} subtitle="In pipeline" icon={Users} />
            </div>
            <div onClick={() => handleStatsClick('login')} className="cursor-pointer">
              <StatsCard title="Login" value={`${actualStats.loginCount}/${actualStats.loginTargetCount}`} subtitle={`${formatCurrency(actualStats.loginAmount)}/${formatCurrency(actualStats.loginTargetAmount)}`} icon={LogIn} variant="accent" />
            </div>
            <div onClick={() => handleStatsClick('disbursed')} className="cursor-pointer">
              <StatsCard title="Disbursed" value={`${actualStats.disbursementCount}/${actualStats.disbursementTargetCount}`} subtitle={`${formatCurrency(actualStats.disbursementAmount)}/${formatCurrency(actualStats.disbursementTargetAmount)}`} icon={TrendingUp} variant="gold" />
            </div>
          </div>
        )}

        {/* Daily Commitment - Desktop Only (non-RM, non-Basic) */}
        {!isRM && !isBasic && (
          <div className="w-full min-w-0 hidden md:block">
            <DailyCommitmentCard onCommitmentChange={refreshCommitment} />
          </div>
        )}

        {/* Tabs for Reminders and Leaderboard — hidden for RM and Basic */}
        {!isRM && !isBasic && (
          <Tabs defaultValue="reminders" className="w-full">
            <TabsList className="w-full justify-start gap-2 bg-muted/50 p-1.5 rounded-lg">
              <TabsTrigger 
                value="reminders" 
                className="flex-1 sm:flex-initial data-[state=active]:bg-success data-[state=active]:text-success-foreground transition-all gap-2"
              >
                <Bell className="h-4 w-4" />
                <span className="hidden sm:inline">Today's Reminders</span>
                <span className="sm:hidden">Reminders</span>
              </TabsTrigger>
              <TabsTrigger 
                value="leaderboard" 
                className="flex-1 sm:flex-initial data-[state=active]:bg-success data-[state=active]:text-success-foreground transition-all gap-2"
              >
                <Trophy className="h-4 w-4" />
                <span>Leaderboard</span>
              </TabsTrigger>
            </TabsList>
            <TabsContent value="reminders" className="mt-4">
              <TaskList />
            </TabsContent>
            <TabsContent value="leaderboard" className="mt-4">
              <Leaderboard />
            </TabsContent>
          </Tabs>
        )}

        {/* TaskList for RM and Basic User */}
        {(isRM || isBasic) && (
          <div className="space-y-3">
            <TaskList onAddReminder={isBasic ? () => setAddTaskOpen(true) : undefined} />
          </div>
        )}
      </div>

      {/* Add Task Dialog */}
      <AddTaskDialog open={addTaskOpen} onOpenChange={setAddTaskOpen} />
      
      {/* Create Lead Sheet */}
      <CreateLeadSheet open={createLeadOpen} onOpenChange={setCreateLeadOpen} />

      {/* Incentive Widget Dialog */}
      <Dialog open={incentiveDialogOpen} onOpenChange={setIncentiveDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Incentive Details</DialogTitle>
          </DialogHeader>
          <IncentiveWidget />
        </DialogContent>
      </Dialog>

      {/* Attendance & Commitment Dialog */}
      <Dialog open={attendanceDialogOpen} onOpenChange={(open) => {
        setAttendanceDialogOpen(open);
        if (!open) loadAttendanceStatus();
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0">
          <DialogTitle className="sr-only">Daily Commitment</DialogTitle>
          <DailyCommitmentCard
            onCommitmentChange={() => { refreshCommitment(); loadAttendanceStatus(); }}
            disableCollapse={true}
            onAttendanceMarked={() => {
              setAttendanceDialogOpen(false);
              loadAttendanceStatus();
              toast.success("Attendance marked successfully!");
            }}
          />
        </DialogContent>
      </Dialog>

    </AppLayout>
  );
}